import { HttpClient } from '@adapters/HttpClient';
import {
  Invoice,
  InvoiceShipmentsPage,
  InvoiceShipment,
  InvoiceStoragesPage,
  InvoiceStorage,
} from '../models/InvoicesView';

export interface InvoiceShipmentsFilters {
  invoiceId: number;
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface InvoiceStoragesFilters {
  invoiceId: number;
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface ExportInvoiceShipmentsResponse {
  url_file: string;
}

function buildAdvancedFiltersParams(searchFilter: { filters: any[]; logic: string }) {
  const params: Record<string, any> = {};
  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      if (f.value !== undefined && f.value !== null && !(Array.isArray(f.value) && f.value.length === 0)) {
        params[`advanced_filters[filters][${i}][value]`] = f.value;
      }
    });
  }
  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }
  return params;
}

/** Meta de paginación típica de la API de facturas (camelCase y snake_case). */
type InvoiceListMeta = {
  total?: number;
  currentPage?: number;
  current_page?: number;
  lastPage?: number;
  last_page?: number;
  perPage?: number;
  per_page?: number;
};

/**
 * Resuelve si hay página siguiente sin depender solo de last_page:
 * si la API no envía lastPage pero sí total, se calcula; si tampoco hay total,
 * una página “llena” implica que puede haber más registros.
 */
function resolveNextPageFromMeta(
  meta: InvoiceListMeta | undefined,
  limit: number,
  itemCount: number,
): { nextPage?: number; total: number } {
  const m = meta ?? {};
  const currentPage = m.currentPage ?? m.current_page ?? 1;
  const total = m.total ?? 0;
  const perPage = m.perPage ?? m.per_page ?? limit;
  const explicitLast = m.lastPage ?? m.last_page;

  if (typeof explicitLast === 'number' && explicitLast >= 1) {
    return {
      nextPage: currentPage < explicitLast ? currentPage + 1 : undefined,
      total,
    };
  }

  if (total > 0 && perPage > 0) {
    const last = Math.max(1, Math.ceil(total / perPage));
    return {
      nextPage: currentPage < last ? currentPage + 1 : undefined,
      total,
    };
  }

  return {
    nextPage: itemCount >= limit ? currentPage + 1 : undefined,
    total,
  };
}

export class InvoicesViewApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getInvoiceShipmentsPaginated(filters: InvoiceShipmentsFilters): Promise<InvoiceShipmentsPage> {
    const { invoiceId, searchFilter, search, sortBy = 'id', sortDirection = 'desc', page = 1, limit = 25 } = filters;

    const params: Record<string, any> = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(search ? { search } : {}),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<{
      data: InvoiceShipment[];
      meta: InvoiceListMeta;
    }>(`/invoices/invoices/${invoiceId}/shipments`, params, {}, showGlobalLoading);

    const items = response.data ?? [];
    const { nextPage, total } = resolveNextPageFromMeta(response.meta, limit, items.length);

    return {
      shipments: items,
      nextPage,
      hasMore: Boolean(nextPage),
      total,
    };
  }

  async getInvoiceById(id: number): Promise<Invoice> {
    return this.httpClient.get<Invoice>(`/invoices/${id}`);
  }

  async getInvoiceStoragesPaginated(filters: InvoiceStoragesFilters): Promise<InvoiceStoragesPage> {
    const {
      invoiceId,
      searchFilter,
      search,
      sortBy = 'product_id',
      sortDirection = 'asc',
      page = 1,
      limit = 25,
    } = filters;

    const params: Record<string, any> = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(search ? { search } : {}),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<{
      data: InvoiceStorage[];
      meta: InvoiceListMeta;
    }>(`/invoices/invoices/${invoiceId}/storages`, params, {}, showGlobalLoading);

    const items = response.data ?? [];
    const { nextPage, total } = resolveNextPageFromMeta(response.meta, limit, items.length);

    return {
      storages: items,
      nextPage,
      hasMore: Boolean(nextPage),
      total,
    };
  }

  /**
   * POST /api/v1/invoices/invoices/:invoiceId/shipments/export
   * Query: igual que GET shipments (sorting[column], sorting[direction], page, per_page, search, advanced_filters)
   * Body: { columns: string[] }
   */
  async exportInvoiceShipments(
    filters: InvoiceShipmentsFilters,
    columns: string[],
  ): Promise<ExportInvoiceShipmentsResponse> {
    const { invoiceId, searchFilter, search, sortBy = 'id', sortDirection = 'desc', page = 1, limit = 25 } = filters;

    const params: Record<string, any> = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(search ? { search } : {}),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        const strValue = Array.isArray(value) ? value.join(',') : String(value);
        searchParams.append(key, strValue);
      }
    });
    const qs = searchParams.toString();
    const url = `/invoices/invoices/${invoiceId}/shipments/export${qs ? `?${qs}` : ''}`;

    return await this.httpClient.post<ExportInvoiceShipmentsResponse>(url, { columns }, false);
  }

  async invoicePreInvoice(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.httpClient.post<any>(`/invoices/pre-invoices/${id}/invoice`, {});

      if (response && typeof response.success === 'undefined') {
        return {
          success: true,
          message: response.message || 'Prefactura facturada exitosamente',
        };
      }

      return response;
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data;

        if (errorData.error && errorData.error.message) {
          return {
            success: false,
            message: errorData.error.message,
          };
        }

        if (errorData.message) {
          return {
            success: false,
            message: errorData.message,
          };
        }
      }

      return {
        success: false,
        message: error.message || 'Error al facturar la prefactura',
      };
    }
  }
}
